'use client';

import React from 'react';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledContentWrapper,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledSection,
  StyledInner,
  StyledStepsGrid,
  StyledStep,
  StyledFormulaBox,
  StyledFormulaBody,
  StyledDataTable,
  StyledBadge,
  StyledTableFootnote,
  StyledFactorsGrid,
  StyledFactorCard,
  StyledTipsGrid,
  StyledTipCard,
  StyledMetricsTable,
  StyledRelatedGrid,
  StyledRelatedCard,
} from './AnchorTextAnalyzerContent.styles';

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

const steps = [
  {
    number: '1',
    title: 'Paste your HTML content',
    description:
      'Copy the HTML source of your page or a specific content section and paste it into the text area. The tool scans for all anchor tags including navigation, body links, footer links, and sidebar links.',
  },
  {
    number: '2',
    title: 'Enter keyword and brand (optional)',
    description:
      'Adding your target keyword helps the tool distinguish exact match and partial match anchors from generic ones. Adding your brand name identifies branded anchors in the distribution breakdown.',
  },
  {
    number: '3',
    title: 'Review your distribution',
    description:
      'See your anchor text type breakdown as a percentage distribution chart, a complete table of all extracted anchors, and an assessment of over-optimization risk based on your exact match percentage.',
  },
];

const anchorTypeGuide = [
  { type: 'Exact Match', description: 'Anchor text is identical to the target keyword', target: 'Under 10%', notes: 'Most powerful for SEO but highest over-optimization risk above 30%', color: '#4c4fff', bg: '#eef0ff' },
  { type: 'Partial Match', description: 'Anchor text includes the keyword plus other words', target: '10-20%', notes: 'Strong SEO value with natural appearance. Ideal backbone of anchor profile', color: '#8b5cf6', bg: '#f3f4ff' },
  { type: 'Branded', description: 'Anchor text uses a brand name or branded phrase', target: '40-60%', notes: 'Most natural anchor type. The dominant type in healthy link profiles', color: '#16a34a', bg: '#f0fdf4' },
  { type: 'Generic', description: 'Non-descriptive text: click here, read more, learn more', target: '20-30%', notes: 'Does not pass keyword signals but avoids over-optimization. Use sparingly', color: '#b45309', bg: '#fffbeb' },
  { type: 'Naked URL', description: 'The full URL used as the anchor text', target: '5-10%', notes: 'Natural for citations and references. Looks authentic in editorial contexts', color: '#0ea5e9', bg: '#f0f9ff' },
  { type: 'Image', description: 'Link wraps an image with no visible text', target: 'Variable', notes: 'Alt text acts as anchor. Add descriptive alt attributes for SEO value', color: '#6b7280', bg: '#f9fafb' },
];

const anchorMistakes = [
  {
    icon: '🎯',
    title: 'Over-using exact match anchors',
    description:
      'Using your target keyword as anchor text repeatedly across many links triggers Google Penguin algorithmic filters. Modern SEO best practice recommends keeping exact match anchors below 10% of your total anchor profile. A sudden spike in exact match anchors is one of the clearest over-optimization signals.',
    stat: 'Keep exact match anchors under 10% of total profile',
  },
  {
    icon: '👆',
    title: 'Relying on click here and learn more',
    description:
      'Generic anchors pass no keyword relevance signal to the destination page. A profile dominated by generic anchors wastes significant internal link equity. Replace generic anchors in your body content with descriptive partial match or branded alternatives that tell both users and search engines what the linked page is about.',
    stat: 'Replace generic anchors in body content with descriptive text',
  },
  {
    icon: '🔗',
    title: 'Using the same anchor text for all links to one page',
    description:
      'All internal links to your target page using identical anchor text looks unnatural and can suppress rankings. A healthy internal link profile uses varied anchor text types pointing to the same destination, just like a natural backlink profile would look across different external sites.',
    stat: 'Vary anchor text across all links pointing to the same page',
  },
  {
    icon: '📸',
    title: 'Image links with empty alt text',
    description:
      'When a hyperlink wraps an image, the alt attribute serves as the anchor text. Empty alt attributes on linked images waste link equity and miss an opportunity to pass relevant keyword signals. Every linked image must have a descriptive alt attribute that represents what the destination page is about.',
    stat: 'All linked images must have descriptive alt text',
  },
  {
    icon: '🔁',
    title: 'Ignoring navigation and footer link anchor text',
    description:
      'Navigation and footer links often use very short or keyword-stuffed anchor text that is repeated on every page of the site. Since these links appear on every page, their anchor text is heavily weighted. Generic or exact match navigation anchors can skew your entire site anchor profile.',
    stat: 'Audit navigation and footer link anchor text separately',
  },
  {
    icon: '🧹',
    title: 'Never auditing after content updates',
    description:
      'As pages are updated, links are added and removed without considering the overall anchor text distribution. Run an anchor text audit after major content updates, site migrations, and any large-scale internal linking campaigns to catch distribution shifts before they accumulate into ranking risks.',
    stat: 'Audit anchor text distribution after every major content update',
  },
];

const tips = [
  {
    number: '01',
    title: 'Use accordion FAQs with descriptive internal links',
    description:
      'Accordion FAQ sections are ideal for natural anchor text because each question organically includes descriptive link text. Instead of "click here," the anchor text reads naturally as part of the question, producing partial match and contextual anchors without any optimization effort.',
    cta: 'Try Accordion widget',
    ctaHref: '/widgets/accordion',
  },
  {
    number: '02',
    title: 'Build tab navigation with keyword-rich labels',
    description:
      'Tab labels naturally become descriptive anchor text when users share or link to specific tab content. Tabs let you present keyword-rich section headings that serve double duty as both UX navigation and natural anchor text opportunities across your page.',
    cta: 'Try Tabs widget',
    ctaHref: '/widgets/tabs',
  },
  {
    number: '03',
    title: 'Link from comparison tables using product names',
    description:
      'Comparison table cells with product names and features create natural branded and partial match anchor text opportunities. Instead of linking from generic button text, link from the product name itself, which is both descriptive and natural.',
    cta: 'Try Comparison Tables widget',
    ctaHref: '/widgets/comparison-tables',
  },
  {
    number: '04',
    title: 'Use content feeds for article title anchors',
    description:
      'Content feeds display article titles as link text, which naturally produces descriptive, varied anchor text. Article titles typically include topic keywords in a natural context, making feeds one of the best sources of healthy partial match internal anchors.',
    cta: 'Try Feeds widget',
    ctaHref: '/widgets/feeds',
  },
  {
    number: '05',
    title: 'Write anchor text for users, not algorithms',
    description:
      'The best anchor text tells a reader exactly what they will find on the linked page. If your anchor text naturally describes the destination without feeling forced, it is almost certainly safe for SEO. Let user experience drive anchor text decisions rather than keyword placement goals.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '06',
    title: 'Vary anchor text for your most important pages',
    description:
      'Identify your three to five most important target pages and audit every internal link pointing to each one. Ensure each page receives a variety of branded, partial match, and descriptive anchors. No single anchor text variant should account for more than half the links to any one page.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '07',
    title: 'Use contextual links over navigation links for keyword anchors',
    description:
      'Keyword-relevant anchor text is most natural and most impactful when it appears in the body content of related pages. Reserve navigation and footer links for branded and generic anchors. Place partial match and descriptive anchors in in-context body text where they read naturally.',
    cta: null,
    ctaHref: null,
  },
  {
    number: '08',
    title: 'Audit after every major link building campaign',
    description:
      'External backlinks shift your anchor text distribution just as much as internal links. After any outreach, guest posting, or digital PR campaign, run an anchor text audit to see how your profile has shifted. Adjust internal linking to rebalance if any type becomes over-represented.',
    cta: null,
    ctaHref: null,
  },
];

const glossaryMetrics = [
  {
    term: 'Anchor Text',
    definition: 'The visible, clickable text of a hyperlink. Used by search engines to understand what the linked page is about and to pass topical relevance signals between pages.',
    formula: '<a href="URL">Anchor Text</a>',
    whenToUse: 'Evaluating internal link strategy and backlink profile SEO health.',
  },
  {
    term: 'Link Equity',
    definition: 'The SEO value passed from one page to another through a hyperlink. Also called link juice or PageRank. The anchor text of a link influences which topics the equity is applied to at the destination page.',
    formula: 'Qualitative (PageRank-derived)',
    whenToUse: 'Understanding which internal links have the most impact on ranking potential.',
  },
  {
    term: 'Google Penguin',
    definition: 'A Google algorithm update targeting manipulative link building practices, including over-optimized anchor text profiles. First launched in 2012, now integrated into core algorithm. Penalizes unnatural exact match anchor text concentration.',
    formula: 'Algorithmic filter',
    whenToUse: 'Understanding why over-optimized anchor text can suppress rankings.',
  },
  {
    term: 'Exact Match Anchor',
    definition: 'An anchor where the link text is identical to the target keyword. Strongest keyword signal but highest over-optimization risk. Should typically account for no more than 5-10% of a page or site anchor profile.',
    formula: 'Exact string match to target keyword',
    whenToUse: 'Measuring over-optimization risk in internal and external link profiles.',
  },
  {
    term: 'TrustRank',
    definition: 'A link analysis algorithm that measures the trustworthiness of pages by proximity to known trusted sites. Natural, diverse anchor text is a trust signal that distinguishes editorial links from manufactured ones.',
    formula: 'Qualitative (distance from seed sites)',
    whenToUse: 'Understanding why anchor text diversity contributes to overall link trust.',
  },
];

interface IProps {
  relatedPosts: IRelatedPost[];
}

export const AnchorTextAnalyzerContent = ({ relatedPosts }: IProps) => {
  return (
    <StyledContentWrapper>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>How It Works</StyledSectionLabel>
          <StyledSectionHeading>How to use this free anchor text analyzer</StyledSectionHeading>
          <StyledSectionIntro>
            No account needed, no sign-up required. Completely free. Paste your HTML to
            instantly extract all anchor tags, categorize them by type, and see your
            exact match percentage and over-optimization risk.
          </StyledSectionIntro>
          <StyledStepsGrid>
            {steps.map((step) => (
              <StyledStep key={step.number}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </StyledStep>
            ))}
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner maxWidth="720px">
          <StyledSectionLabel>Anchor Text Formula</StyledSectionLabel>
          <StyledSectionHeading>
            How over-optimization risk is calculated
          </StyledSectionHeading>
          <StyledSectionIntro>
            This free tool classifies every anchor tag and calculates the distribution across
            six types. The exact match percentage is the primary risk signal used to assess
            your anchor text health.
          </StyledSectionIntro>

          <StyledFormulaBox>
            <p className="label">Over-Optimization Risk</p>
            <p className="formula">Exact Match % = (Exact Match Links / Total Links) x 100</p>
            <p className="example">
              Example: <span>3 exact match out of 30 total links = 10%. Within safe range.</span>
            </p>
          </StyledFormulaBox>

          <StyledFormulaBody>
            <p>
              The tool classifies anchors into six types using the following hierarchy: if the
              anchor contains an image and no text, it is classified as Image. If the text is a
              full URL, it is Naked URL. If it is in the generic anchor list, it is Generic.
              If it matches your brand name, it is Branded. If it exactly matches your keyword,
              it is Exact Match. If it contains your keyword, it is Partial Match. Everything
              else defaults to Generic.
            </p>
            <p>
              Status thresholds: Exact match at 0-15% is Good (balanced). 15-30% is Fair (watch
              carefully). Above 30% is Poor (over-optimized). These thresholds apply specifically
              to exact match anchors. Other types have less direct risk profiles but contribute
              to overall distribution health.
            </p>
          </StyledFormulaBody>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Anchor Type Reference</StyledSectionLabel>
          <StyledSectionHeading>
            Healthy distribution targets for each anchor text type
          </StyledSectionHeading>
          <StyledSectionIntro>
            Use these benchmarks to evaluate your anchor text profile. A natural profile
            mirrors the pattern of editorial links that site owners did not control or optimize.
          </StyledSectionIntro>

          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Anchor Type</th>
                  <th>Description</th>
                  <th>Target Range</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {anchorTypeGuide.map((row) => (
                  <tr key={row.type}>
                    <td>
                      <StyledBadge color={row.color} bg={row.bg}>{row.type}</StyledBadge>
                    </td>
                    <td>{row.description}</td>
                    <td className="col-value">{row.target}</td>
                    <td className="col-note">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>

          <StyledTableFootnote>
            Distribution targets based on Ahrefs, Moz, and SEMrush anchor text research, 2026.
          </StyledTableFootnote>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>What Kills Your Anchor Text Profile</StyledSectionLabel>
          <StyledSectionHeading>
            Six anchor text mistakes that create over-optimization risk
          </StyledSectionHeading>
          <StyledSectionIntro>
            These mistakes accumulate silently across content updates and link building campaigns.
            Catching them early protects your rankings from algorithmic filters.
          </StyledSectionIntro>

          <StyledFactorsGrid>
            {anchorMistakes.map((factor) => (
              <StyledFactorCard key={factor.title}>
                <span className="factor-icon">{factor.icon}</span>
                <h3>{factor.title}</h3>
                <p>{factor.description}</p>
                <span className="factor-stat">{factor.stat}</span>
              </StyledFactorCard>
            ))}
          </StyledFactorsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>Anchor Text Tips</StyledSectionLabel>
          <StyledSectionHeading>
            8 tips to build a natural, diverse anchor text profile
          </StyledSectionHeading>
          <StyledSectionIntro>
            Apply these strategies to build an internal link profile that passes strong SEO
            signals without triggering over-optimization filters. All CommonNinja widgets are free to start.
          </StyledSectionIntro>

          <StyledTipsGrid>
            {tips.map((tip) => (
              <StyledTipCard key={tip.number}>
                <div className="tip-header">
                  <div className="tip-number">{tip.number}</div>
                  <h3>{tip.title}</h3>
                </div>
                <p>{tip.description}</p>
                {tip.cta && tip.ctaHref && (
                  <a className="tip-cta" href={tip.ctaHref} target="_blank" rel="noopener noreferrer">
                    {tip.cta} →
                  </a>
                )}
              </StyledTipCard>
            ))}
          </StyledTipsGrid>
        </StyledInner>
      </StyledSection>

      <StyledSection bg="var(--gray-50)">
        <StyledInner>
          <StyledSectionLabel>Link SEO Glossary</StyledSectionLabel>
          <StyledSectionHeading>
            Key anchor text and link building terms explained
          </StyledSectionHeading>
          <StyledSectionIntro>
            These definitions help you understand the algorithm-level reasons why anchor text
            diversity and distribution matter for rankings.
          </StyledSectionIntro>

          <StyledMetricsTable>
            <table>
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Definition</th>
                  <th>Formula / Format</th>
                  <th>When to Use</th>
                </tr>
              </thead>
              <tbody>
                {glossaryMetrics.map((row) => (
                  <tr key={row.term}>
                    <td className="metric-name">{row.term}</td>
                    <td>{row.definition}</td>
                    <td><span className="metric-formula">{row.formula}</span></td>
                    <td>{row.whenToUse}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledMetricsTable>
        </StyledInner>
      </StyledSection>

      {relatedPosts.length > 0 && (
        <StyledSection>
          <StyledInner>
            <StyledSectionLabel>From the Blog</StyledSectionLabel>
            <StyledSectionHeading>Further reading on anchor text and internal linking</StyledSectionHeading>
            <StyledSectionIntro>
              Go deeper on link building strategy, internal link architecture, and the
              ranking signals that make anchor text one of the most powerful on-page tools.
            </StyledSectionIntro>
            <StyledRelatedGrid>
              {relatedPosts.map((post) => (
                <StyledRelatedCard key={post.slug} href={`/blog/${post.slug}`}>
                  <h3>{post.name}</h3>
                  {post.excerpt && (
                    <p dangerouslySetInnerHTML={{ __html: clampExcerpt(post.excerpt) }} />
                  )}
                  <span className="read-more">Read article →</span>
                </StyledRelatedCard>
              ))}
            </StyledRelatedGrid>
          </StyledInner>
        </StyledSection>
      )}

    </StyledContentWrapper>
  );
};
